import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  useUpdate,
  useNotify,
  SimpleForm,
  Edit,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  AutocompleteInput,
  TextInput,
  useTranslate,
  ReferenceInput,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import RadioGroupX from "../common/RadioGroupX";
import Box from "@mui/material/Box";
import TabForm from "../common/TabForm";
import currentDate from "../../../utils/currentDate";
import {
  filterToQuery,
  inputText,
  matchSuggestion,
  OptionRenderer,
} from "../common/GiroCustomSelectOption";
import PickCOA from "../common/PickCOA";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(14), maxLength(14)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateCustomer = [required(), minLength(36), maxLength(36)];
const validateNote = [required()];

export default function BadDebtCheckCashingEditDialog({
  open,
  setOpen,
  recordId,
}) {
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const t = useTranslate();

  const save = useCallback(
    async (values) => {
      try {
        await update(
          resource,
          { id: values.id, data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.updated", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          if (error.body.errors === "403") {
            // munculkan notifikasi erro ketika sudah approved
            notify("mine.notification.403_error", { type: "error" });
          } else {
            // The shape of the returned validation errors must match the shape of the form
            return error.body.errors;
          }
        }
      }
    },
    [update]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth="md"
    >
      <Edit
        title={" "}
        id={recordId}
        actions={false}
        sx={{ "& .RaEdit-main": { mt: 0 } }}
      >
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns callback={handleClose} />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {t("mine.dialog.edit_bad_debt_check_cashing")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <TextInput source="id" sx={{ display: "none" }} />

            <CodeNumberInput
              // open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
              disabled
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              label="mine.label.date"
              fullWidth
              validate={validateDate}
              defaultValue={currentDate()}
            />
            <ReferenceInput
              source="bad_debt_installment_id"
              reference="bad-debt-installments"
              perPage={10}
              sort={{ field: "bad_debt_installments.code", order: "ASC" }}
              filter={{
                "BadDebtCheckCashing.bad_debt_installment_id": "IS NULL",
                "bad_debt_installments.type": 3,
                "bad_debt_installments.approved": true,
              }}
            >
              <AutocompleteInput
                label="mine.label.check_number"
                variant="outlined"
                isRequired
                fullWidth
                validate={validateCustomer}
                optionValue="id"
                // optionText="code"
                optionText={<OptionRenderer />}
                inputText={inputText}
                matchSuggestion={matchSuggestion}
                filterToQuery={filterToQuery}
              />
            </ReferenceInput>
            <RadioGroupX
              sx={{
                "& .MuiFormGroup-root": { justifyContent: "space-around" },
              }}
              choices={[
                { id: 1, name: t("mine.label.cashing") },
                { id: 2, name: t("mine.label.cancel") },
                { id: 3, name: t("mine.label.rejected") },
              ]}
            />
            <PickCOA />
            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              rows={3}
              validate={validateNote}
            />

            <TabForm />
          </DialogContent>
        </SimpleForm>
      </Edit>
    </Dialog>
  );
}
